import { NotificationViewModel, type NotificationEntry, type ReadMarkStore } from '@lumora/core';
import { NotificationBell as SharedNotificationBell, type NotificationBellClassNames } from '@lumora/ui';
import { useMemo } from 'react';
import { SITE, client } from '../../client';

export type AppointmentNotification = NotificationEntry;

const readNotificationStorageKey = 'alamir-read-notifications';

/** Remembers opened notifications in this browser (same key as before). */
export const localReadStore: ReadMarkStore = {
    load() {
        try {
            const parsed = JSON.parse(window.localStorage.getItem(readNotificationStorageKey) || '[]');
            return new Set<string>(Array.isArray(parsed) ? parsed.map(String) : []);
        } catch {
            return new Set<string>();
        }
    },
    add(id) {
        try {
            const ids = new Set(localReadStore.load());
            ids.add(id);
            window.localStorage.setItem(readNotificationStorageKey, JSON.stringify([...ids]));
        } catch {
            // storage unavailable
        }
    },
};

const classNames: NotificationBellClassNames = {
    root: 'relative',
    button: 'relative rounded-full bg-white/10 p-2 text-white transition hover:bg-white/20',
    badge: 'absolute -right-1 -top-1 rounded-full bg-yellow-300 px-1.5 py-0.5 text-[10px] font-black text-gray-950',
    panel: 'absolute right-0 top-10 z-[130] w-80 overflow-hidden rounded-2xl bg-white text-gray-950 shadow-2xl ring-1 ring-gray-200',
    head: 'border-b border-gray-100 p-4',
    title: 'text-sm font-black uppercase tracking-widest',
    subtitle: 'mt-1 text-xs font-bold text-gray-500',
    list: 'max-h-96 overflow-y-auto p-2',
    error: 'm-2 rounded-xl bg-amber-50 p-3 text-xs font-bold text-amber-800',
    empty: 'p-4 text-sm font-bold text-gray-400',
    row: 'cursor-pointer rounded-xl p-3 transition hover:bg-gray-50 flex items-start justify-between gap-3',
    rowRead: 'bg-white',
    rowUnread: 'bg-yellow-50',
    rowTitle: 'text-xs font-black text-gray-950',
    rowTime: 'mt-1 text-[10px] font-black uppercase tracking-widest text-gray-500',
    rowSale: 'mt-1 text-[10px] font-black uppercase tracking-widest text-green-700',
    rowStatus: 'mt-1 text-[10px] font-black uppercase tracking-widest text-red-600',
    readButton: 'rounded-full bg-gray-900 px-2 py-1 text-[10px] font-black uppercase text-white',
};

interface NotificationBellProps {
    onNotificationClick?: (notification: NotificationEntry) => void;
}

export default function NotificationBell({ onNotificationClick }: NotificationBellProps) {
    const viewModel = useMemo(() => new NotificationViewModel(client, SITE, localReadStore), []);
    return (
        <SharedNotificationBell
            viewModel={viewModel}
            onNotificationClick={onNotificationClick}
            reloadEvents={['appointments-updated', 'sale-requests-updated']}
            classNames={classNames}
        />
    );
}
